import { createSelector, createSlice } from '@reduxjs/toolkit'
import { languagesByCode } from '@/utils/languages'
import { HYDRATE } from 'next-redux-wrapper'
import _isEmpty from 'lodash/isEmpty'
import { deleteEmptyProps } from '@/utils/objectHelper'

const setFilterOptions = (allProducts = []) => {
  const brands = new Set()
  const models = new Set()
  const modelTypes = new Set()
  const lineOffDates = new Set()
  const years = new Set()
  allProducts.forEach(p => {
    p.brand && brands.add(p.brand)
    p.model && models.add(p.model)
    p.modelType && modelTypes.add(p.modelType)
    p.year && years.add(p.year)
    p.lineOffDate && lineOffDates.add(p.lineOffDate)
  })
  return {
    brand: [...brands],
    model: [...models],
    modelType: [...modelTypes],
    year: [...years],
    lineOffDate: [...lineOffDates]
  }
}

const initialState = {
  all: [],
  filtered: [],
  optionsPerFilter: {
    brand: [],
    model: [],
    modelType: [],
    year: [],
    lineOffDate: [],
    language: [],
    metadata: []
  },
  filterBy: {
    brand: ''
  }
}

const products = createSlice({
  name: 'products',
  initialState,
  reducers: {
    SET: (
      state,
      {
        payload: {
          all,
          optionsPerFilter: { language, metadata }
        }
      }
    ) => {
      state.all = all || []
      state.optionsPerFilter = { language, metadata, ...setFilterOptions(all) }
    },
    SET_BRAND: (state, { payload }) => {
      state.filterBy.brand = payload
    }
  },
  extraReducers: builder => {
    builder.addCase(HYDRATE, (state, action) => {
      // Only products.all and products.optionsPerFilter can
      // Carefully picking the state that can be overwritten by the server.
      // The others are client-side only.
      if (action.payload.products) {
        const { all, optionsPerFilter, filterBy } = action.payload.products
        const nonEmptyOptionsPerFilter = Object.entries(optionsPerFilter).reduce((acc, [key, value]) => {
          if (!_isEmpty(value)) {
            acc[key] = value
          }
          return acc
        }, {})

        return {
          ...state,
          filterBy: { ...state.filterBy, ...deleteEmptyProps(filterBy), brand: state.filterBy.brand || filterBy.brand },
          all: [...state.all, ...all],
          optionsPerFilter: {
            ...state.optionsPerFilter,
            ...nonEmptyOptionsPerFilter
          }
        }
      }
      return state
    })
  }
})

const selectOptionsPerFilter = state => state.products.optionsPerFilter

const selectLanguages = createSelector([selectOptionsPerFilter], optionsPerFilter => {
  const uniqueLang = {}
  return optionsPerFilter.language
    .reduce((acc, l) => {
      // Assumming there can be languages like en_US or en_GB
      const value = l.split('_')[0]
      const label = languagesByCode[value]
      if (uniqueLang[value]) return acc
      acc.push({ value, label })
      uniqueLang[value] = value
      return acc
    }, [])
    .sort((l1, l2) => l1.label.localeCompare(l2.label))
})

export default products

export const productActions = products.actions

export const productSelectors = {
  selectProducts: state => state.products,
  selectAll: state => state.products.all,
  selectFiltered: state => state.products.filtered,
  selectFilterBy: state => state.products.filterBy,
  selectOptionsPerFilter,
  selectLanguages
}
